import React, { useState, useEffect } from 'react';
import { Steps, Card, Button, Typography, Space, Row, Col, Statistic, Progress, Alert } from 'antd';
import {
  EnvironmentOutlined,
  DatabaseOutlined,
  DollarOutlined,
  CheckCircleOutlined,
  ArrowRightOutlined,
  ThunderboltOutlined,
  TrophyOutlined,
  ArrowUpOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLocation } from '../contexts/LocationContext';
import { LocationSetup } from './LocationSetup';
import { DataUpload } from './DataUpload';
import axios from 'axios';
import { config } from '../config/env';
import { savingsReasonText } from './InsufficientDataCard';
import {
  trackOnboardingCompleted,
  trackOnboardingStepCompleted,
  trackSkipAction,
} from '../utils/analytics';

const { Title, Paragraph, Text } = Typography;
const { Step } = Steps;

interface OnboardingFlowProps {
  onComplete?: () => void;
}

export const OnboardingFlow: React.FC<OnboardingFlowProps> = ({ onComplete }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [locationCompleted, setLocationCompleted] = useState(false);
  const [dataCompleted, setDataCompleted] = useState(false);
  const [estimatedSavings, setEstimatedSavings] = useState<{
    monthlySavings: number | null;
    annualSavings: number | null;
    paybackMonths: number | null;
    roi: number | null;
  } | null>(null);
  const [loadingSavings, setLoadingSavings] = useState(false);
  const [savingsUnavailable, setSavingsUnavailable] = useState<string | null>(
    null,
  );

  const { user, setUser } = useAuth();
  const { lat, lng, city, state, utility } = useLocation();
  const navigate = useNavigate();
  
  // Poll subscription status if user subscribes during onboarding
  useEffect(() => {
    if (!user?.isSubscribed && currentStep >= 3) {
      const pollInterval = setInterval(async () => {
        try {
          const token = localStorage.getItem('access_token');
          if (!token) return;
          
          const response = await axios.get(
            `${config.apiUrl}/api/subscriptions/status`,
            { headers: { Authorization: `Bearer ${token}` } },
          );
          
          if (response.data?.isActive) {
            // Refresh user data
            const userResponse = await axios.get(
              `${config.apiUrl}/api/auth/me`,
              { headers: { Authorization: `Bearer ${token}` } },
            );
            setUser(userResponse.data);
            
            // Mark onboarding as complete and navigate
            localStorage.setItem('onboarding_completed', 'true');
            trackOnboardingCompleted(4);
            
            // Navigate to dashboard or expedient page
            navigate('/dashboard');
          }
        } catch (error) {
          // Silently fail - just retry
        }
      }, 3000); // Poll every 3 seconds
      
      return () => clearInterval(pollInterval);
    }
  }, [user?.isSubscribed, currentStep, setUser, navigate]);

  // Check if user is already subscribed - auto-complete onboarding
  useEffect(() => {
    if (user?.isSubscribed) {
      localStorage.setItem('onboarding_completed', 'true');
      if (onComplete) {
        onComplete();
      } else {
        navigate('/dashboard');
      }
    }
  }, [user?.isSubscribed, onComplete, navigate]);
  
  // Check if location is already set
  useEffect(() => {
    if (lat && lng) {
      setLocationCompleted(true);
      // Auto-advance to next step if location is set and we're on step 0
      if (currentStep === 0) {
        setCurrentStep(1);
      }
    }
  }, [lat, lng, currentStep]);

  // Fetch estimated savings when location is available
  useEffect(() => {
    const fetchSavings = async () => {
      if (lat && lng && user?.id && currentStep >= 2) {
        setLoadingSavings(true);
        try {
          const token = localStorage.getItem('access_token');
          const response = await axios.get(
            `${config.apiUrl}/api/energy/savings/metrics?days=30`,
            { headers: { Authorization: `Bearer ${token}` } },
          );

          const payload = response.data.savings;
          if (payload?.status === 'ok' && payload.metrics) {
            const m = payload.metrics;
            setEstimatedSavings({
              monthlySavings: m.dollarsSaved ?? null,
              annualSavings:
                m.dollarsSaved != null ? m.dollarsSaved * 12 : null,
              paybackMonths: m.paybackMonths ?? null,
              roi: m.roi ?? null,
            });
            setSavingsUnavailable(null);
          } else {
            // P1 M&V: no verified metrics → explain why, never estimate.
            setEstimatedSavings(null);
            setSavingsUnavailable(savingsReasonText(payload));
          }
        } catch (err: any) {
          console.error('Failed to fetch estimated savings:', err);
          setEstimatedSavings(null);
          setSavingsUnavailable(
            'Savings service unreachable — no figures are shown rather than estimated ones.',
          );
        } finally {
          setLoadingSavings(false);
        }
      }
    };

    fetchSavings();
  }, [lat, lng, user?.id, currentStep]);

  const handleLocationComplete = () => {
    setLocationCompleted(true);
    trackOnboardingStepCompleted(0, 'location');
    setCurrentStep(1);
  };

  const handleDataComplete = () => {
    setDataCompleted(true);
    trackOnboardingStepCompleted(1, 'data');
    setCurrentStep(2);
  };

  const handleSkipLocation = () => {
    trackSkipAction('location', 0);
    setCurrentStep(1);
  };

  const handleSkipData = () => {
    trackSkipAction('data', 1);
    setCurrentStep(2);
  };

  const handleCompleteOnboarding = () => {
    // Mark onboarding as complete in localStorage
    localStorage.setItem('onboarding_completed', 'true');
    trackOnboardingCompleted(currentStep + 1);
    if (onComplete) {
      onComplete();
    } else {
      // Navigate to subscription page (ExpedientPage)
      navigate('/expedient');
    }
  };
  
  const handleSubscribeClick = () => {
    // Track step completion
    trackOnboardingStepCompleted(3, 'subscribe');
    // Mark onboarding step 3 as complete, then navigate to subscription
    localStorage.setItem('onboarding_completed', 'true');
    trackOnboardingCompleted(4);
    navigate('/expedient');
  };

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);

  const steps = [
    {
      title: 'Set Location',
      icon: <EnvironmentOutlined />,
      description: 'Tell us where you are',
    },
    {
      title: 'Connect Data',
      icon: <DatabaseOutlined />,
      description: 'Upload or connect your data',
    },
    {
      title: 'See Your Value',
      icon: <DollarOutlined />,
      description: 'Discover your potential savings',
    },
    {
      title: 'Subscribe',
      icon: <ThunderboltOutlined />,
      description: 'Choose your plan and get started',
    },
  ];

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <div>
            <Card>
              <Title level={3}>📍 Set Your Location</Title>
              <Paragraph>
                To provide accurate insights and savings calculations, we need to know your location.
                This helps us:
              </Paragraph>
              <ul style={{ marginBottom: '24px' }}>
                <li>Calculate location-specific utility rates</li>
                <li>Provide accurate weather data</li>
                <li>Compare your performance with peers in your area</li>
                <li>Deliver personalized recommendations</li>
              </ul>
              <LocationSetup onComplete={handleLocationComplete} showSkip={true} />
              {locationCompleted && (
                <Alert
                  message="Location set successfully!"
                  type="success"
                  showIcon
                  style={{ marginTop: '16px' }}
                  action={
                    <Button size="small" onClick={() => setCurrentStep(1)}>
                      Continue
                      <ArrowRightOutlined />
                    </Button>
                  }
                />
              )}
            </Card>
          </div>
        );

      case 1:
        return (
          <div>
            <Card>
              <Title level={3}>📊 Connect Your Data</Title>
              <Paragraph>
                Connect your energy data to unlock personalized insights. You can:
              </Paragraph>
              <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
                <Col xs={24} md={12}>
                  <Card
                    size="small"
                    style={{ height: '100%', textAlign: 'center' }}
                    hoverable
                  >
                    <DatabaseOutlined style={{ fontSize: '32px', color: '#1890ff', marginBottom: '12px' }} />
                    <Title level={5}>Upload CSV</Title>
                    <Text type="secondary">
                      Upload historical energy data from your utility or monitoring system
                    </Text>
                  </Card>
                </Col>
                <Col xs={24} md={12}>
                  <Card
                    size="small"
                    style={{ height: '100%', textAlign: 'center' }}
                    hoverable
                  >
                    <ThunderboltOutlined style={{ fontSize: '32px', color: '#52c41a', marginBottom: '12px' }} />
                    <Title level={5}>Connect Sensors</Title>
                    <Text type="secondary">
                      Integrate with Modbus, MQTT, or REST API sensors (coming soon)
                    </Text>
                  </Card>
                </Col>
              </Row>
              <Alert
                message="Data upload is optional"
                description="You can skip this step and explore the platform with demo data. You can always add your data later."
                type="info"
                showIcon
                style={{ marginBottom: '16px' }}
              />
              <div style={{ minHeight: '400px' }}>
                <DataUpload />
              </div>
              <Space style={{ marginTop: '16px', width: '100%', justifyContent: 'flex-end' }}>
                <Button onClick={handleSkipData}>Skip for Now</Button>
                <Button type="primary" onClick={handleDataComplete}>
                  Continue
                  <ArrowRightOutlined />
                </Button>
              </Space>
            </Card>
          </div>
        );

      case 2:
        return (
          <div>
            <Card>
              <Title level={3}>💰 Your Potential Savings</Title>
              {city && state && (
                <Paragraph style={{ fontSize: '16px', marginBottom: '24px' }}>
                  Based on your location in <strong>{city}, {state}</strong>
                  {utility && ` (${utility})`}
                </Paragraph>
              )}
              
              {loadingSavings ? (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                  <Progress type="circle" percent={75} />
                  <Paragraph style={{ marginTop: '16px' }}>Calculating your potential savings...</Paragraph>
                </div>
              ) : estimatedSavings ? (
                <div>
                  <Card
                    style={{
                      marginBottom: '24px',
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      color: 'white',
                    }}
                  >
                    <Row gutter={[24, 24]}>
                      <Col xs={24} sm={12} md={6}>
                        <Statistic
                          title={<span style={{ color: 'white' }}>Monthly Savings</span>}
                          value={estimatedSavings.monthlySavings ?? '—'}
                          prefix={<DollarOutlined style={{ color: 'white' }} />}
                          valueStyle={{ color: 'white', fontSize: '24px' }}
                          formatter={(value) => (typeof value === 'number' ? formatCurrency(value) : '—')}
                        />
                      </Col>
                      <Col xs={24} sm={12} md={6}>
                        <Statistic
                          title={<span style={{ color: 'white' }}>Annual Projection</span>}
                          value={estimatedSavings.annualSavings ?? '—'}
                          prefix={<ArrowUpOutlined style={{ color: 'white' }} />}
                          valueStyle={{ color: 'white', fontSize: '24px' }}
                          formatter={(value) => (typeof value === 'number' ? formatCurrency(value) : '—')}
                        />
                      </Col>
                      <Col xs={24} sm={12} md={6}>
                        <Statistic
                          title={<span style={{ color: 'white' }}>Payback Period</span>}
                          value={estimatedSavings.paybackMonths ?? '—'}
                          suffix="months"
                          prefix={<TrophyOutlined style={{ color: 'white' }} />}
                          valueStyle={{ color: 'white', fontSize: '24px' }}
                        />
                      </Col>
                      <Col xs={24} sm={12} md={6}>
                        <Statistic
                          title={<span style={{ color: 'white' }}>ROI</span>}
                          value={estimatedSavings.roi ?? '—'}
                          suffix="%"
                          prefix={<ThunderboltOutlined style={{ color: 'white' }} />}
                          valueStyle={{ color: 'white', fontSize: '24px' }}
                        />
                      </Col>
                    </Row>
                  </Card>

                  <Alert
                    message="Ready to unlock these savings?"
                    description="Subscribe now to access full analytics, historical data, and optimization recommendations."
                    type="success"
                    showIcon
                    style={{ marginBottom: '16px' }}
                  />

                  <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
                    <Button onClick={() => setCurrentStep(1)}>Back</Button>
                    <Button type="primary" size="large" onClick={() => setCurrentStep(3)}>
                      Unlock Premium Features
                      <ArrowRightOutlined />
                    </Button>
                  </Space>
                </div>
              ) : savingsUnavailable ? (
                <Alert
                  message="No verified savings yet"
                  description={savingsUnavailable}
                  type="info"
                  showIcon
                />
              ) : (
                <div>
                  <Alert
                    message="Set your location first"
                    description="Go back to step 1 to set your location, then we can calculate your potential savings."
                    type="warning"
                    showIcon
                  />
                  <Space style={{ width: '100%', justifyContent: 'flex-end', marginTop: '16px' }}>
                    <Button onClick={() => setCurrentStep(0)}>Set Location</Button>
                  </Space>
                </div>
              )}
            </Card>
          </div>
        );

      case 3:
        return (
          <div>
            <Card>
              <Title level={3}>🚀 Subscribe to Get Started</Title>
              <Paragraph style={{ fontSize: '16px', marginBottom: '24px' }}>
                You're one step away from maximizing your energy savings and ROI.
              </Paragraph>

              <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
                <Col xs={24} md={12}>
                  <Card title="What You Get" size="small">
                    <ul style={{ paddingLeft: '20px' }}>
                      <li>Full access to Value Dashboard</li>
                      <li>Historical data analysis</li>
                      <li>AI-powered optimization recommendations</li>
                      <li>Real-time sensor integration</li>
                      <li>Priority support</li>
                      <li>API access</li>
                    </ul>
                  </Card>
                </Col>
                <Col xs={24} md={12}>
                  <Card title="Your Investment" size="small">
                    {estimatedSavings && (
                      <div>
                        <Statistic
                          title="Monthly Subscription"
                          value={29}
                          prefix="$"
                          valueStyle={{ color: '#1890ff', fontSize: '28px' }}
                        />
                        <div style={{ marginTop: '16px' }}>
                          <Text type="secondary">
                            Potential savings:{' '}
                            <strong>
                              {estimatedSavings.monthlySavings != null
                                ? `${formatCurrency(estimatedSavings.monthlySavings)}/month`
                                : '—'}
                            </strong>
                          </Text>
                          <br />
                          <Text type="success">
                            Net benefit:{' '}
                            <strong>
                              {estimatedSavings.monthlySavings != null
                                ? `${formatCurrency(estimatedSavings.monthlySavings - 29)}/month`
                                : '—'}
                            </strong>
                          </Text>
                        </div>
                      </div>
                    )}
                  </Card>
                </Col>
              </Row>

              <Alert
                message="30-Day Money-Back Guarantee"
                description="Try risk-free. If you don't save at least $500 in the first 30 days, we'll refund your subscription and you keep access."
                type="success"
                showIcon
                style={{ marginBottom: '24px' }}
              />

              <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                <Button onClick={() => setCurrentStep(2)}>Back</Button>
                <Button
                  type="primary"
                  size="large"
                  onClick={handleSubscribeClick}
                  style={{ minWidth: '200px' }}
                >
                  Subscribe Now
                  <ArrowRightOutlined />
                </Button>
              </Space>
            </Card>
          </div>
        );

      default:
        return null;
    }
  };

  // If user is subscribed, show completion message
  if (user?.isSubscribed) {
    return (
      <div style={{ maxWidth: '900px', margin: '40px auto', padding: '0 20px' }}>
        <Card>
          <div style={{ textAlign: 'center', padding: '40px 20px' }}>
            <CheckCircleOutlined style={{ fontSize: '64px', color: '#52c41a', marginBottom: '24px' }} />
            <Title level={2}>You're All Set! 🎉</Title>
            <Paragraph style={{ fontSize: '16px', marginBottom: '24px' }}>
              You've completed onboarding and have premium access. You can access all features from the menu.
            </Paragraph>
            <Space>
              <Button type="primary" size="large" onClick={() => navigate('/dashboard')}>
                Go to Dashboard
              </Button>
              <Button size="large" onClick={() => navigate('/value-dashboard')}>
                View Value Dashboard
              </Button>
            </Space>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '900px', margin: '40px auto', padding: '0 20px' }}>
      <div style={{ textAlign: 'center', marginBottom: '40px' }}>
        <Title level={2}>Welcome to Expedient! 🎉</Title>
        <Paragraph style={{ fontSize: '16px' }}>
          Let's get you set up in 4 simple steps
        </Paragraph>
      </div>

      <Steps current={currentStep} style={{ marginBottom: '40px' }}>
        {steps.map((step, index) => (
          <Step
            key={index}
            title={step.title}
            description={step.description}
            icon={step.icon}
            status={
              index < currentStep
                ? 'finish'
                : index === currentStep
                ? 'process'
                : 'wait'
            }
          />
        ))}
      </Steps>

      {renderStepContent()}

      {currentStep < 3 && (
        <div style={{ textAlign: 'center', marginTop: '24px' }}>
          <Button
            type="link"
            onClick={handleCompleteOnboarding}
            style={{ color: '#999' }}
          >
            Skip onboarding for now (you can return anytime)
          </Button>
        </div>
      )}
      
      {/* Progress indicator */}
      <div style={{ marginTop: '32px', textAlign: 'center' }}>
        <Text type="secondary">
          Step {currentStep + 1} of {steps.length}
          {locationCompleted && currentStep > 0 && (
            <span style={{ marginLeft: '8px', color: '#52c41a' }}>
              ✓ Location set
            </span>
          )}
          {dataCompleted && currentStep > 1 && (
            <span style={{ marginLeft: '8px', color: '#52c41a' }}>
              ✓ Data connected
            </span>
          )}
        </Text>
      </div>
    </div>
  );
};

export default OnboardingFlow;

